import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CourseDropdownComponent } from 'src/app/common/course-dropdown/course-dropdown.component';
import { SafeHtmlPipe } from 'src/app/templates/safe.pipe';
import { ITeamMember, Team, TeamMember, UserCourse, UserDashboard } from '../../app.models';
import { AppService } from '../../app.service';
import { IntroductionService } from '../../services/introduction.service';

@Component({
  selector: 'app-team-compare',
  templateUrl: './team-compare.component.html',
  styleUrls: ['./team-compare.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatOption,
    MatIconModule,
    MatAutocompleteTrigger,
    CourseDropdownComponent,
    SafeHtmlPipe,
    CommonModule,
  ],
})
export class TeamCompareComponent implements OnInit, AfterViewInit {
  introduction: string = '';
  types: string[] = ['ISTJ', 'ISFJ', 'INFJ', 'INTJ', 'ISTP', 'ISFP', 'INFP', 'INTP', 'ESTP', 'ESFP', 'ENFP', 'ENTP', 'ESTJ', 'ESFJ', 'ENFJ', 'ENTJ'];
  subjectPlaceHolderText: string = 'Choose Subject...';
  typePlaceHolderText: string = 'Compare to...';
  displayResults: boolean = false;
  firstName: string = '';
  lastNameInitial: string = '';
  friendFirstName: string = '';
  friendLastNameInitial: string = '';
  mbtiType: string = '';
  content: string[];
  myTypeContent: string = '';
  comparisonAgainstContent: string = '';
  selectedCourseId: string = '';
  selectedSubject: string = '';
  selectedType: string = '';
  selectedTeamMemberId: string = '';
  loadingData: boolean = false;
  loggedInUserEmail: string = '';
  compareTense: number = 0;
  primaryCtrl = new FormControl();
  secondaryCtrl = new FormControl();
  primaryOptions: Observable<ITeamMember[]>;
  secondaryOptions: Observable<ITeamMember[]>;
  selectedPrimary?: TeamMember;
  selectedSecondary?: TeamMember;

  public form: FormGroup;
  public userDashboard = new UserDashboard();
  private team = new Team();
  // fb: FormBuilder;

  constructor(
    private appService: AppService,
    private introductionService: IntroductionService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
  ) {
    this.form = this.fb.group({
      subject: ['', Validators.required],
      type: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.userDashboard = this.appService.getUserDashboard();
    this.loggedInUserEmail = this.appService.emailAddress;

    this.team.teamMembers = this.appService.getTeamInfo().teamMembers;
    this.introduction = this.introductionService.getContent('Compare');

    if (this.userDashboard.userCourses.length == 0) {
      this.initialize();
    }

    this.mbtiType = this.appService.mbtiType;
    this.firstName = this.appService.firstName;
    this.lastNameInitial = this.appService.lastName.substr(0, 1);

    this.primaryOptions = this.primaryCtrl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterTeamMembers(value, 'primary')),
    );

    this.secondaryOptions = this.secondaryCtrl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterTeamMembers(value, 'secondary')),
    );
  }

  private filterTeamMembers(value: string | TeamMember, role: string): ITeamMember[] {
    // If the value is a team member, we've selected someone from the drop down
    if (value instanceof TeamMember) {
      if (role === 'primary') {
        this.selectedPrimary = value as TeamMember;
        this.setType({ member: this.selectedPrimary, role: role });
      } else if (role === 'secondary') {
        this.selectedSecondary = value as TeamMember;
        this.setType({ member: this.selectedSecondary, role: role });
      }

      return this.team.teamMembers.filter(m => m.mbtiType);
    }

    // Now we know it's definitely a string, so we want to filter the contents of the drop down box instead
    const filtered = value.toLowerCase();

    return this.team.teamMembers.filter(m => m.mbtiType).filter((tm) => {
      // If we're working with the secondary and the primary has already been selected, we should eliminate from the list of options
      if (role === 'secondary' && this.selectedPrimary && this.selectedPrimary.emailAddress === tm.emailAddress) return false;

      return tm.firstName.toLowerCase().includes(filtered) || tm.lastName.toLowerCase().includes(filtered);
    });
  }

  teamMemberClicked(trigger: MatAutocompleteTrigger): void {
    trigger.openPanel();
  }

  primaryOpened() {
    this.primaryCtrl.setValue('');
  }

  secondaryOpened() {
    this.secondaryCtrl.setValue('');
  }

  displayTeamMember(teamMember: ITeamMember): string {
    if (!teamMember) return '';

    return `${teamMember.firstName} ${teamMember.lastName}`;
  }

  ngAfterViewInit() {
    queueMicrotask(() => {
      const teamMember = this.team.teamMembers.find((member) => this.loggedInUserEmail.toLowerCase() == member.emailAddress.toLowerCase());
      if (teamMember) {
        this.primaryCtrl.setValue(teamMember);
      }

      const id = +this.route.snapshot.queryParams['memberId'];
      if (id > 0) {
        let teamMember = this.team.teamMembers.find((obj) => obj.memberId === id);
        if (teamMember) {
          this.secondaryCtrl.setValue(teamMember);
        }
      }
    });
  }

  setSubject($event) {
    this.selectedSubject = $event;
    this.GetSubjectData(this.userDashboard.userCourses.find((x) => x.courseName == this.selectedSubject).courseId.toString(), this.selectedSubject);
  }

  setType($event) {
    if ($event.role == 'primary') {
      this.firstName = $event.member.firstName;
      this.lastNameInitial = $event.member.lastName.substr(0, 1);
      this.mbtiType = $event.member.mbtiType;

      if ($event.member.currentUser) {
        this.compareTense = 1;
      } else if (this.compareTense === 1 && !$event.member.currentUser) {
        this.compareTense = 0;
      }
      this.GetTypeData();
    } else {
      this.friendFirstName = $event.member.firstName;
      this.friendLastNameInitial = $event.member.lastName.substr(0, 1);
      this.selectedType = $event.member.mbtiType;

      if ($event.member.currentUser) {
        this.compareTense = 2;
      } else if (this.compareTense === 2 && !$event.member.currentUser) {
        this.compareTense = 0;
      }
      this.GetTypeData();
    }
  }

  GetSubjectData(courseId: string, courseName: string) {
    this.loadingData = true;
    this.selectedSubject = courseName;
    this.selectedCourseId = courseId;
    this.GetData();
  }

  GetTypeData() {
    this.loadingData = true;
    this.GetData();
  }

  getColor() {
    let classes = {
      gray: this.selectedSubject == this.subjectPlaceHolderText,
      purple: this.selectedSubject != this.typePlaceHolderText,
    };

    return classes;
  }

  private GetData() {
    if (!this.selectedType || !this.selectedCourseId) {
      this.loadingData = false;
      return;
    }

    var tenseSetting = this.compareTense;

    this.appService.getContentForCompare(this.selectedCourseId, this.mbtiType, this.selectedType, this.compareTense).subscribe(
      (resultArray) => {
        this.displayResults = true;
        this.content = resultArray;

        var find = '{{firstName}}';
        var regexp = new RegExp(find, 'g');

        if (tenseSetting === 1) {
          this.myTypeContent = this.content[0];
          this.comparisonAgainstContent = this.content[1].replace(regexp, this.friendFirstName);
        } else if (tenseSetting === 2) {
          this.myTypeContent = this.content[0].replace(regexp, this.firstName);
          this.comparisonAgainstContent = this.content[1];
        } else {
          this.myTypeContent = this.content[0].replace(regexp, this.firstName);
          this.comparisonAgainstContent = this.content[1].replace(regexp, this.friendFirstName);
        }

        this.loadingData = false;
      },
      (error) => console.log('Error :: ' + error),
    );
  }

  private initialize() {
    this.appService.getDashboard().subscribe(
      (resultArray) => {
        for (var category of resultArray) {
          for (var course of category.courses) {
            var userCourse = new UserCourse();
            userCourse.courseId = course.courseId;
            userCourse.courseName = course.courseName;
            userCourse.isCompleted = course.isCompleted;
            userCourse.allowCompare = course.allowCompare;
            this.userDashboard.userCourses.push(userCourse);
          }
        }
      },
      (error) => console.log('Error :: ' + error),
    );
  }
}
